import React from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { Formik, Field } from "formik";
import { signUp, signInWithGoogle } from "../../redux/actions/authActions";
import { getReferralInvitationPartner } from "../../services/authService";
import AuthLayout from "../../layouts/Auth";
import { CheckboxWithLabel } from "formik-material-ui";
import SelectWithAPI from "../../components/SelectWithAPI";
import GoogleSignInButton from "../../components/GoogleSignInButton";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { Alert as MuiAlert } from "@material-ui/lab";
import { persistPendingSocialRegistration } from "../../utils/socialAuth";
import {
  captureReferralAttribution,
  clearReferralAttribution,
} from "../../utils/referralAttribution";

import {
  Avatar,
  Button,
  Paper,
  TextField as MuiTextField,
  Typography,
  InputLabel,
  FormControl as MuiFormControl,
  Grid,
  Box,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";

const TextField = styled(MuiTextField)(spacing);
const Alert = styled(MuiAlert)(spacing);

const Wrapper = styled(Paper)`
  width: 100%;
  padding: ${(props) => props.theme.spacing(4)}px;
  border-radius: 30px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 28px 64px rgba(15, 23, 42, 0.12);

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(6)}px;
  }
`;

const BigAvatar = styled(Avatar)`
  width: 84px;
  height: 84px;
  text-align: center;
  margin: 0;
  box-shadow: 0 18px 34px rgba(37, 99, 235, 0.18);
`;

const Hero = styled(Box)`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 24px;
  padding: 24px;
  border-radius: 24px;
  background: linear-gradient(135deg, #f8fbff 0%, #eef4ff 45%, #f8fafc 100%);
  border: 1px solid rgba(37, 99, 235, 0.1);
  margin-bottom: 24px;

  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 110px minmax(0, 1fr) 220px;
    align-items: center;
  }
`;

const HeroCopy = styled(Box)`
  min-width: 0;
`;

const Eyebrow = styled(Box)`
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.1);
  color: #2563eb;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const HeroTitle = styled(Typography)`
  margin-top: 14px;
  font-size: clamp(2rem, 3vw, 2.8rem);
  font-weight: 800;
  line-height: 1.06;
  color: #0f172a;
`;

const HeroDescription = styled(Typography)`
  margin-top: 12px;
  max-width: 620px;
  color: #64748b;
  line-height: 1.75;
`;

const HeroAside = styled(Box)`
  padding: 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.06);
`;

const HeroAsideLabel = styled(Typography)`
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const HeroAsideValue = styled(Typography)`
  margin-top: 8px;
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
`;

const HeroAsideText = styled(Typography)`
  margin-top: 8px;
  color: #64748b;
  line-height: 1.6;
`;

const SectionCard = styled(Box)`
  padding: 22px;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: #ffffff;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.06);
`;

const SectionHeader = styled(Box)`
  margin-bottom: 18px;
`;

const SectionEyebrow = styled(Typography)`
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #2563eb;
  font-weight: 700;
`;

const SectionTitle = styled(Typography)`
  margin-top: 6px;
  font-size: 1.3rem;
  font-weight: 800;
  color: #0f172a;
`;

const SectionDescription = styled(Typography)`
  margin-top: 6px;
  color: #64748b;
  line-height: 1.6;
`;

const StyledFormControl = styled(MuiFormControl)`
  width: 100%;
`;

const CheckboxRow = styled(Box)`
  margin-top: 8px;
  padding: 14px 16px;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.16);
`;

const ActionBar = styled(Box)`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SubmitButton = styled(Button)`
  min-height: 52px;
  border-radius: 18px;
  text-transform: none;
  font-size: 1rem;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  box-shadow: 0 18px 34px rgba(37, 99, 235, 0.26);

  &:hover {
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
  }
`;

const SecondaryText = styled(Typography)`
  color: #64748b;
  text-align: center;
`;

const SecondaryLink = styled(Link)`
  color: #2563eb;
  font-weight: 700;
  text-decoration: none;
`;

function SignUp() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [referralCode] = React.useState(() => captureReferralAttribution());
  const [invitingPartner, setInvitingPartner] = React.useState(null);

  React.useEffect(() => {
    if (referralCode) {
      getReferralInvitationPartner(referralCode)
        .then(setInvitingPartner)
        .catch(() => setInvitingPartner(null));
    }
  }, [referralCode]);
  const phoneRegexp = /^(?:\+\d{1,5}\s?)?(?:\(\d{1,5}\)|\d{1,5})[- .]?\d{1,5}[- .]?\d{1,5}$/;

  const resolveSignUpErrorMessage = (error) =>
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "No fue posible completar el registro.";

  const handleGoogleCredential = async (credential) => {
    try {
      const response = await dispatch(signInWithGoogle(credential));

      if (response?.status === "LOGIN_SUCCESS" && response?.token) {
        history.push("/dashboard");
        return;
      }

      if (response?.status === "REGISTRATION_REQUIRED") {
        persistPendingSocialRegistration({
          credential,
          profile: response.profile,
          referral: referralCode,
        });
        history.push("/auth/social/complete-registration");
        return;
      }

      throw new Error(
        response?.message || "No fue posible iniciar el registro con Google."
      );
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "No fue posible iniciar el registro con Google.",
        "error"
      );
    }
  };

  return (
    <AuthLayout>
      <Wrapper>
        <Helmet title="Sign Up" />
        <Hero>
          <BigAvatar alt="Wifiads" src="/static/img/avatars/logo.png" />
          <HeroCopy>
            <Eyebrow>Client Onboarding</Eyebrow>
            <HeroTitle>Formulario de registro</HeroTitle>
            <HeroDescription>
              Cree su cuenta para gestionar campañas, publicidades y resultados
              desde una experiencia más clara, rápida y organizada.
            </HeroDescription>
          </HeroCopy>
          <HeroAside>
            <HeroAsideLabel>Alta de cliente</HeroAsideLabel>
            <HeroAsideValue>Cuenta lista en minutos</HeroAsideValue>
            <HeroAsideText>
              Complete los datos de acceso y los datos comerciales para empezar
              a operar dentro del sistema.
            </HeroAsideText>
          </HeroAside>
        </Hero>

        {invitingPartner && (
          <Alert severity="success" mb={4}>
            <strong>{invitingPartner.name}</strong>
            {invitingPartner.company
              ? `, de ${invitingPartner.company},`
              : ""}{" "}
            te invitó a registrar tu negocio en WifiAds. La vinculación se
            aplicará automáticamente.
          </Alert>
        )}

        <Formik
          initialValues={{
            name: "",
            lastName: "",
            company: "",
            email: "",
            password: "",
            confirmPassword: "",
            businessType: "",
            phone: "",
            mobilePhone: "",
            identification: "",
            streetAddress: "",
            city: "",
            country: "",
            referral: referralCode,
            eula: false,
            submit: false,
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().max(255).required("Campo requerido."),
            lastName: Yup.string().max(255).required("Campo requerido."),
            businessType: Yup.string().required("Campo requerido"),
            identification: Yup.string()
              //.matches(cedulaRegexp, "###-#######-#")
              .required("Campo requerido"),
            streetAddress: Yup.string().required("Campo requerido"),
            city: Yup.string().required("Campo requerido"),
            country: Yup.string().required("Campo requerido"),
            eula: Yup.boolean().required("Campo requerido"),
            phone: Yup.string()
              .matches(phoneRegexp, "Teléfono inválido")
              .max(255)
              .required("Campo requerido"),
            mobilePhone: Yup.string()
              .matches(phoneRegexp, "Teléfono inválido")
              .max(255)
              .required("Campo requerido"),
            email: Yup.string()
              .email("Correo invalido")
              .max(255)
              .required("Campo requerido"),
            password: Yup.string()
              .min(12, "Utiliza al menos 12 caracteres.")
              .max(64, "Utiliza un máximo de 64 caracteres.")
              .required("Campo requerido."),
            confirmPassword: Yup.string().when("password", {
              is: (val) => (val && val.length > 0 ? true : false),
              then: Yup.string().oneOf(
                [Yup.ref("password")],
                "Ambas contraseñas deben ser iguales."
              ),
            }),
          })}
          onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
            try {
              await dispatch(
                signUp({
                  firstName: values.name,
                  lastName: values.lastName,
                  company: values.company,
                  email: values.email,
                  password: values.password,
                  confirmPassword: values.confirmPassword,
                  businessType: values.businessType,
                  phone: values.phone,
                  mobileNumber: values.mobilePhone,
                  identificationType: values.identificationType,
                  identificationNumber: values.identification,
                  streetAddress: values.streetAddress,
                  city: values.city,
                  country: values.country,
                  eulaAgreed: values.eula,
                  referral: values.referral,
                })
              );
              clearReferralAttribution();
              Swal.fire(
                "Registro en proceso",
                "Si su cuenta estaba pendiente de validacion, reenviamos el correo para continuar el registro.",
                "success"
              );
              history.push("/auth/sign-in");
            } catch (error) {
              const message = resolveSignUpErrorMessage(error);
              setStatus({ success: false });
              setErrors({ submit: message });
              setSubmitting(false);
              Swal.fire("Error!", message, "error");
            }
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              {errors.submit && (
                <Alert mt={2} mb={2} severity="warning">
                  {errors.submit}
                </Alert>
              )}

              <SectionCard>
                <SectionHeader>
                  <SectionEyebrow>Acceso</SectionEyebrow>
                  <SectionTitle>Información del usuario</SectionTitle>
                  <SectionDescription>
                    Configure los datos de acceso con los que ingresará al
                    sistema.
                  </SectionDescription>
                </SectionHeader>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      type="email"
                      name="email"
                      label="Correo electronico"
                      value={values.email}
                      error={Boolean(touched.email && errors.email)}
                      fullWidth
                      helperText={touched.email && errors.email}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      my={0}
                      variant="outlined"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="password"
                      name="password"
                      label="Contraseña"
                      value={values.password}
                      error={Boolean(touched.password && errors.password)}
                      fullWidth
                      helperText={
                        (touched.password && errors.password) ||
                        "Utiliza al menos 12 caracteres. Puedes escribir una frase fácil de recordar."
                      }
                      onBlur={handleBlur}
                      onChange={handleChange}
                      my={0}
                      variant="outlined"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="password"
                      name="confirmPassword"
                      label="Confirmar Contraseña"
                      value={values.confirmPassword}
                      error={Boolean(
                        touched.confirmPassword && errors.confirmPassword
                      )}
                      fullWidth
                      helperText={
                        touched.confirmPassword && errors.confirmPassword
                      }
                      onBlur={handleBlur}
                      onChange={handleChange}
                      my={0}
                      variant="outlined"
                      required
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              <Box mt={3} />

              <SectionCard>
                <SectionHeader>
                  <SectionEyebrow>Perfil comercial</SectionEyebrow>
                  <SectionTitle>Información del cliente</SectionTitle>
                  <SectionDescription>
                    Complete los datos del titular y de la empresa para
                    habilitar el registro.
                  </SectionDescription>
                </SectionHeader>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="text"
                      name="name"
                      label="Nombre"
                      value={values.name}
                      error={Boolean(touched.name && errors.name)}
                      fullWidth
                      helperText={touched.name && errors.name}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      my={0}
                      variant="outlined"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="text"
                      name="lastName"
                      label="Apellido"
                      value={values.lastName}
                      error={Boolean(touched.lastName && errors.lastName)}
                      fullWidth
                      variant="outlined"
                      required
                      helperText={touched.lastName && errors.lastName}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      my={0}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="text"
                      name="company"
                      label="Compañia"
                      value={values.company}
                      error={Boolean(touched.company && errors.company)}
                      fullWidth
                      helperText={touched.company && errors.company}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      my={0}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <StyledFormControl variant="outlined" required>
                      <InputLabel id="negocioLabel">Tipo de negocio</InputLabel>
                      <SelectWithAPI
                        id="businessType"
                        name="businessType"
                        labelid="negocioLabel"
                        label="Tipo de negocio"
                        value={values.businessType}
                        error={Boolean(
                          touched.businessType && errors.businessType
                        )}
                        helperText={touched.businessType && errors.businessType}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        my={0}
                        url="/businessType"
                      />
                    </StyledFormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="text"
                      name="phone"
                      label="Teléfono"
                      value={values.phone}
                      error={Boolean(touched.phone && errors.phone)}
                      fullWidth
                      helperText={touched.phone && errors.phone}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      my={0}
                      variant="outlined"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="text"
                      name="mobilePhone"
                      label="Celular"
                      value={values.mobilePhone}
                      error={Boolean(touched.mobilePhone && errors.mobilePhone)}
                      fullWidth
                      helperText={touched.mobilePhone && errors.mobilePhone}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      my={0}
                      variant="outlined"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <StyledFormControl variant="outlined" required>
                      <InputLabel id="identificationLabel">
                        Tipo identificación
                      </InputLabel>
                      <SelectWithAPI
                        id="identificationType"
                        name="identificationType"
                        labelid="identificationLabel"
                        label="Tipo identificación"
                        value={values.identificationType}
                        error={Boolean(
                          touched.identificationType &&
                            errors.identificationType
                        )}
                        helperText={
                          touched.identificationType &&
                          errors.identificationType
                        }
                        onBlur={handleBlur}
                        onChange={handleChange}
                        my={0}
                        url="/identificationType"
                      />
                    </StyledFormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="text"
                      name="identification"
                      label="Identificación"
                      value={values.identification}
                      error={Boolean(
                        touched.identification && errors.identification
                      )}
                      fullWidth
                      helperText={
                        touched.identification && errors.identification
                      }
                      onBlur={handleBlur}
                      onChange={handleChange}
                      my={0}
                      variant="outlined"
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      type="text"
                      name="streetAddress"
                      label="Dirección"
                      value={values.streetAddress}
                      error={Boolean(
                        touched.streetAddress && errors.streetAddress
                      )}
                      fullWidth
                      helperText={touched.streetAddress && errors.streetAddress}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      my={0}
                      variant="outlined"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="text"
                      name="city"
                      label="Ciudad"
                      value={values.city}
                      error={Boolean(touched.city && errors.city)}
                      fullWidth
                      helperText={touched.city && errors.city}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      my={0}
                      variant="outlined"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <StyledFormControl variant="outlined" required>
                      <InputLabel id="countryLabel">País</InputLabel>
                      <SelectWithAPI
                        id="country"
                        name="country"
                        label="País"
                        labelid="countryLabel"
                        value={values.country}
                        error={Boolean(touched.country && errors.country)}
                        helperText={touched.country && errors.country}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        my={0}
                        url="/country"
                      />
                    </StyledFormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      type="text"
                      name="referral"
                      label={
                        referralCode
                          ? "Código de invitación del partner"
                          : "Correo del partner (opcional)"
                      }
                      value={values.referral}
                      error={Boolean(touched.referral && errors.referral)}
                      fullWidth
                      helperText={
                        referralCode
                          ? "La invitación fue aplicada automáticamente."
                          : touched.referral && errors.referral
                      }
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled={Boolean(referralCode)}
                      my={0}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              <ActionBar>
                <CheckboxRow>
                  <Field
                    type="checkbox"
                    component={CheckboxWithLabel}
                    name="eula"
                    checked={values.eula}
                    Label={{
                      label: "He leido y acepto los terminos y condiciones",
                    }}
                    value={values.eula}
                    key={values.eula}
                    error={Boolean(touched.eula && errors.eula)}
                    fullWidth
                    helperText={touched.eula && errors.eula}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </CheckboxRow>

                <SubmitButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || !values.eula}
                >
                  Registrar
                </SubmitButton>

                <GoogleSignInButton
                  onCredential={handleGoogleCredential}
                  text="signup_with"
                  title="O regístrese con Google"
                />

                <SecondaryText variant="body2">
                  ¿Ya tienes una cuenta?{" "}
                  <SecondaryLink to="/auth/sign-in">
                    Inicia sesión
                  </SecondaryLink>
                </SecondaryText>
              </ActionBar>
            </form>
          )}
        </Formik>
      </Wrapper>
    </AuthLayout>
  );
}

export default SignUp;
